import { cutToast } from 'utility/toast';
import { useMutation, useApolloClient } from '@apollo/client';
import { IDropdownOption } from '@fluentui/react';
import { invoiceDateFormat } from 'common/utils/dateFormats';
import { BlockBlobClient } from '@azure/storage-blob';
import React from 'react';

import { UploadDocumentFormModal } from './UploadDocumentFormModal';
import { EntityDocumentValues } from '../types';
import DOCUMENT_UPLOAD_STATUS from 'common/graphql/DocumentUploadStatusSubscription.graphql';
import UPLOAD_DOCUMENT from './UserUploadDocument.graphql';

import DOCUMENTSLIST from '../MyDocumentsList/PersonalDocuments.graphql';
import { getFileTypesList } from 'documents/documentAssignment/folder/list/FolderView/FolderViewHeader/UploadForm/utils';
import { ExtractionTypes_Query } from 'documents/documentAssignment/folder/list/FolderView/FolderViewHeader/UploadForm/__generated__/ExtractionTypes';

import { OnDocumentUploadStatus } from 'common/graphql/__generated__/DocumentUploadStatusSubscription';
import { DocumentTypeOptions } from 'common/types/utility';
import {
  PersonalDocuments,
  PersonalDocumentsVariables,
} from '../MyDocumentsList/__generated__/PersonalDocuments';
import {
  UserUploadDocument,
  UserUploadDocumentVariables,
} from './__generated__/UserUploadDocument';

interface UploadDocumentModalProps {
  visible: boolean;
  onDismiss: () => void;
  documentOptions?: DocumentTypeOptions[];
  companyCurrencies?: IDropdownOption[];
  defaultDocumentTypeId?: number;
  extractionTypesData?: ExtractionTypes_Query;
}

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  visible,
  onDismiss,
  documentOptions,
  companyCurrencies,
  defaultDocumentTypeId,
  extractionTypesData,
}) => {
  const client = useApolloClient();
  const [uploadDocument] = useMutation<
    UserUploadDocument,
    UserUploadDocumentVariables
  >(UPLOAD_DOCUMENT);

  const handleUpload = async (
    values: EntityDocumentValues,
    fileSelected: File[]
  ) => {
    const documentType = documentOptions?.find(
      (opt) => opt.key === values.entityDocumentTypeId
    );
    if (!documentType) return;

    const extractionTypeId = values.extractionTypeId;
    const content = values._documentContents;
    const indexName = values.indexName;
    const indexDescription = values.indexDescription;
    const indexReferenceNumber = values.indexReferenceNumber;
    const indexTransactionDate = values.indexTransactionDate;
    const indexAmount = values.indexAmount
      ? parseFloat(values.indexAmount)
      : null;
    const indexCurrencyId = values.indexCurrencyId;
    const comment = values.comment;

    let filteredFilesList: File[] = fileSelected;
    if (extractionTypeId) {
      const selectedExtractionType =
        extractionTypesData?.extractionTypes?.nodes.find(
          (node) => node.id === extractionTypeId
        );
      const fileTypesAllowed = getFileTypesList(selectedExtractionType);

      filteredFilesList = fileSelected.filter((file) =>
        fileTypesAllowed?.includes(file.type)
      );
      if (fileSelected.length > filteredFilesList.length)
        cutToast.error('Files with invalid data types are not uploaded');
    }

    filteredFilesList.map(async (fileEntity, fileIndex) => {
      const toastId = cutToast.info(`Uploading ${fileEntity?.name}...`);

      const uploadMutationResults = await uploadDocument({
        variables: {
          document: {
            documentTypeId: parseInt(documentType.key.toString()),
            description: content ? content : '',
            comment: comment,
            filename: fileEntity.name,
            indexName: indexName ? indexName : '',
            indexDescription: indexDescription ? indexDescription : '',
            indexReferenceNumber: indexReferenceNumber
              ? indexReferenceNumber
              : '',
            indexTransactionDate: indexTransactionDate
              ? invoiceDateFormat(indexTransactionDate)
              : null,
            indexAmount: indexAmount ? indexAmount : null,
            indexCurrencyId: indexCurrencyId ? indexCurrencyId : null,
            extractionTypeId: extractionTypeId,
          },
        },
      });

      if (uploadMutationResults.errors)
        cutToast.error(`Upload of ${fileEntity.name} failed`, { id: toastId });

      if (
        uploadMutationResults.data?.userUploadDocument?.document &&
        uploadMutationResults.data.userUploadDocument.document._documentFileId
      ) {
        const observer = client.subscribe({
          query: DOCUMENT_UPLOAD_STATUS,
          variables: {
            documentId:
              uploadMutationResults.data.userUploadDocument.document
                ._documentFileId!,
          },
        });

        const subscription = observer.subscribe((data) => {
          const subscribedData = data.data as OnDocumentUploadStatus;
          const { status, document } = {
            ...subscribedData.documentUploadStatus,
          };

          if (status.type === 'VALIDATING') {
            cutToast.loading(
              status.message
                ? `Validating files ${fileEntity.name} - ${status.message}`
                : `Validating files ${fileEntity.name}`,
              { id: toastId }
            );
          } else if (status.type === 'EXTRACTING') {
            cutToast.loading(
              status.message
                ? `Extracting data from ${fileEntity.name} - ${status.message}`
                : `Extracting data from ${fileEntity.name}`,
              { id: toastId }
            );
          } else if (status.type === 'FAILURE') {
            subscription.unsubscribe();
            cutToast.error(
              status.message
                ? `Upload of ${fileEntity.name} failed - ${status.message}`
                : `Upload of ${fileEntity.name} failed`,
              { id: toastId }
            );
          } else if (status.type === 'WARNING') {
            subscription.unsubscribe();
            cutToast.warning(
              status.message
                ? `Warning for file ${fileEntity.name}: ${status.message}`
                : `Warning for file ${fileEntity.name}`,
              { id: toastId }
            );
          } else {
            subscription.unsubscribe();
            cutToast.success(
              status.message
                ? `Successfully uploaded ${fileEntity.name}: ${status.message}`
                : `Successfully uploaded ${fileEntity.name}`,
              { id: toastId }
            );
          }
        });
        const clientBlob = new BlockBlobClient(
          uploadMutationResults.data.userUploadDocument.uploadLink
        );
        await clientBlob.uploadData(fileEntity);
        cutToast.loading(`Validating ${fileEntity.name}...`, { id: toastId });
        await client.refetchQueries({
          include: [DOCUMENTSLIST],
        });
      }
    });
  };

  return (
    <UploadDocumentFormModal
      visible={visible}
      onDismiss={onDismiss}
      title="Upload Documents"
      subtitle="Select a document to upload for this Folder"
      documentOptions={documentOptions}
      companyCurrencies={companyCurrencies}
      defaultDocumentTypeId={defaultDocumentTypeId}
      isEdit={false}
      onSubmit={handleUpload}
    />
  );
};
